import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../button";
import ScanQRCodeModal from "../modal/ScanQRCodeModal";
import QRScanner from "qr-scanner";
import { useTranslation } from "react-i18next";
import MusterStationScannedQRDetailsModal from "../modal/MusterStationScannedQRDetailsModal";
import { useGetUserDetailsByEvacuationId } from "../../hooks/emergency-evacuation/query";
import StatusCodes from "../../constants/StatusCodes";
import toast from "react-hot-toast";

const QRScannerComponent = ({ musterPointId }: { musterPointId: number }) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerRef = useRef<QRScanner | null>(null);
  const [qrCode, setQrCode] = useState("");

  const [isScanQRCodeModalOpen, setIsScanQRCodeModalOpen] =
    useState<boolean>(false);
  const [
    isMusterStationScannedQRDetailsModalOpen,
    setIsMusterStationScannedQRDetailsModalOpen,
  ] = useState<boolean>(false);

  const { data: appointment, error: fetchAppointmentError } =
    useGetUserDetailsByEvacuationId({
      encryptedId: qrCode,
    });

  useEffect(() => {
    if (appointment && qrCode) {
      const isMusterPointExists = appointment?.musterPoints?.some(
        (item: { n_MusterPointID: number }) =>
          item.n_MusterPointID === musterPointId,
      );

      if (isMusterPointExists) {
        setIsMusterStationScannedQRDetailsModalOpen(true);
      } else {
        toast.error(t("participantNotFound"));
        setQrCode("");
      }
    } else if (fetchAppointmentError && qrCode) {
      if ((fetchAppointmentError as any).statusCode === StatusCodes.NOT_FOUND) {
        toast.error(t("participantNotFound"));
      }
      setQrCode("");
    }
  }, [appointment, fetchAppointmentError, musterPointId, qrCode, t]);

  const stopScanning = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.stop(); // Stop the scanner
      scannerRef.current = null; // Clear the reference
    }

    // Clean up video stream
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
    }
  }, []);

  const handleToggleQRCodeModal = useCallback(
    (flag: boolean) => {
      setIsScanQRCodeModalOpen(flag);
      stopScanning();
    },
    [stopScanning],
  );

  const startScanning = useCallback(async () => {
    try {
      if (!musterPointId) {
        toast.error(t("pleaseSelectMusterPoint"));
        return;
      }

      handleToggleQRCodeModal(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const scanner = new QRScanner(
        videoRef.current as HTMLVideoElement,
        (result) => {
          const extractedId = result.split(";")[1].substring(1);
          setQrCode(extractedId);

          stopScanning();
          handleToggleQRCodeModal(false);
        },
        undefined, // Error callback (optional)
        undefined, // Optional scan region calculation
        "environment", // Set preferred camera to back (environment) camera
      );

      scannerRef.current = scanner; // Store the scanner instance
      scanner.start();
    } catch (error) {
      console.error(error);
    }
  }, [handleToggleQRCodeModal, musterPointId, stopScanning, t]);

  return (
    <>
      <Button onClick={startScanning}>{t("scanQRCode")}</Button>

      <ScanQRCodeModal
        isOpen={isScanQRCodeModalOpen}
        onOpenChange={() => handleToggleQRCodeModal(false)}
        videoRef={videoRef}
      />

      <MusterStationScannedQRDetailsModal
        musterPointId={musterPointId}
        appointment={appointment}
        isOpen={isMusterStationScannedQRDetailsModalOpen}
        onOpenChange={() => {
          setQrCode("");
          setIsScanQRCodeModalOpen(false);
          setIsMusterStationScannedQRDetailsModalOpen(false);
        }}
      />
    </>
  );
};

export default memo(QRScannerComponent);
